import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2, TrendingUp, DollarSign, Search, ExternalLink } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import QuarterlyRevenueLeaderboard from '@/components/QuarterlyRevenueLeaderboard';

interface ProtocolRevenue {
  id: string;
  date: string;
  token: {
    symbol: string;
    name: string;
    market_cap: number;
    current_price: number;
    available_on_swyftx: boolean;
    available_on_coinspot: boolean;
    coingecko_id: string;
  };
  daily_revenue: number;
  monthly_revenue: number;
  annualized_revenue: number;
  yield_to_holders_apr: number;
  price_to_fees_ratio: number;
  revenue_model: string;
  revenue_share_percentage: number;
  revenue_source: string[];
}

// Affiliate links
const AFFILIATE_LINKS = {
  swyftx: 'https://trade.swyftx.com.au/register/?ref=cryptotipsau',
  coinspot: 'https://www.coinspot.com.au/join/REFWV4YMH',
};

export default function ProtocolRevenueDashboard() {
  const [protocols, setProtocols] = useState<ProtocolRevenue[]>([]);
  const [loading, setLoading] = useState(false);
  const [collecting, setCollecting] = useState(false);
  const [checking, setChecking] = useState(false);
  const { toast } = useToast();

  const fetchProtocolRevenue = async () => {
    setLoading(true);
    try {
      const today = new Date().toISOString().split('T')[0];
      
      const { data, error } = await supabase
        .from('protocol_revenue')
        .select(`
          *,
          token:tokens(symbol, name, market_cap, current_price, available_on_swyftx, available_on_coinspot, coingecko_id)
        `)
        .eq('date', today)
        .order('yield_to_holders_apr', { ascending: false, nullsFirst: false });

      if (error) throw error;

      setProtocols(data || []);
    } catch (error) {
      console.error('Error fetching protocol revenue:', error);
      toast({
        title: "Error",
        description: "Failed to fetch protocol revenue data",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const collectRevenue = async () => {
    setCollecting(true);
    try {
      const { data, error } = await supabase.functions.invoke('collect-protocol-revenue');

      if (error) throw error;

      toast({
        title: "Success",
        description: `Collected revenue data for ${data.protocolsProcessed} protocols`,
      });

      await fetchProtocolRevenue();
    } catch (error) {
      console.error('Error collecting revenue:', error);
      toast({
        title: "Error",
        description: "Failed to collect protocol revenue",
        variant: "destructive",
      });
    } finally {
      setCollecting(false);
    }
  };

  const checkAvailableTokens = async () => {
    setChecking(true);
    try {
      const { data, error } = await supabase.functions.invoke('check-swyftx-revenue-tokens');

      if (error) throw error;

      toast({
        title: "Swyftx Token Availability",
        description: `${data.availableOnSwyftx}/${data.totalRevenueTokens} revenue tokens available on Swyftx`,
      });

      console.log('Available tokens:', data.tokens);
      console.log('Not available:', data.notAvailable);
    } catch (error) {
      console.error('Error checking tokens:', error);
      toast({
        title: "Error",
        description: "Failed to check token availability",
        variant: "destructive",
      });
    } finally {
      setChecking(false);
    }
  };

  useEffect(() => {
    fetchProtocolRevenue();
  }, []);

  const getRevenueModelColor = (model: string) => {
    switch (model) {
      case 'fee_sharing':
        return 'bg-blue-500';
      case 'staking_rewards':
        return 'bg-green-500';
      case 'buyback':
        return 'bg-purple-500';
      default:
        return 'bg-gray-500';
    }
  };

  const formatCurrency = (value: number) => {
    if (value >= 1_000_000) {
      return `$${(value / 1_000_000).toFixed(2)}M`;
    } else if (value >= 1_000) {
      return `$${(value / 1_000).toFixed(2)}K`;
    }
    return `$${value.toFixed(2)}`;
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Protocol Revenue Tracker</h1>
          <p className="text-muted-foreground mt-1">Real yield metrics for DeFi protocols</p>
        </div>
        <div className="space-x-2">
          <Button onClick={fetchProtocolRevenue} disabled={loading} variant="outline">
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Refresh
          </Button>
          <Button onClick={checkAvailableTokens} disabled={checking} variant="outline">
            {checking ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Search className="mr-2 h-4 w-4" />}
            Check Available Tokens
          </Button>
          <Button onClick={collectRevenue} disabled={collecting}>
            {collecting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <DollarSign className="mr-2 h-4 w-4" />}
            Collect Revenue Data
          </Button>
        </div>
      </div>

      <QuarterlyRevenueLeaderboard />

      {protocols.length === 0 && !loading && (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-12">
              <DollarSign className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">No Revenue Data</h3>
              <p className="text-muted-foreground mb-4">
                No protocol revenue data found for today. Click "Collect Revenue Data" to fetch the latest metrics.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {protocols.map((protocol) => (
          <Card key={protocol.id} className="hover:shadow-lg transition-shadow">
            <CardHeader>
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle className="text-xl">{protocol.token.symbol}</CardTitle>
                  <p className="text-sm text-muted-foreground">{protocol.token.name}</p>
                </div>
                <Badge className={getRevenueModelColor(protocol.revenue_model)}>
                  {protocol.revenue_model.replace('_', ' ')}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Yield APR - Main Metric */}
              <div className="text-center p-4 bg-primary/10 rounded-lg">
                <div className="flex items-center justify-center gap-2">
                  <TrendingUp className="h-5 w-5 text-primary" />
                  <div className="text-3xl font-bold text-primary">
                    {protocol.yield_to_holders_apr?.toFixed(2) || '0.00'}%
                  </div>
                </div>
                <div className="text-sm text-muted-foreground mt-1">
                  Yield to Holders APR
                </div>
              </div>

              {/* Revenue Metrics */}
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-muted-foreground">Daily Revenue</p>
                  <p className="font-semibold">{formatCurrency(protocol.daily_revenue)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Monthly</p>
                  <p className="font-semibold">{formatCurrency(protocol.monthly_revenue)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Market Cap</p>
                  <p className="font-semibold">{formatCurrency(protocol.token.market_cap)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Price</p>
                  <p className="font-semibold">${protocol.token.current_price.toFixed(4)}</p>
                </div>
              </div>

              {/* Valuation Metrics */}
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">P/F Ratio:</span>
                  <span className="font-semibold">
                    {protocol.price_to_fees_ratio?.toFixed(2) || 'N/A'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Revenue Share:</span>
                  <span className="font-semibold">
                    {protocol.revenue_share_percentage.toFixed(1)}%
                  </span>
                </div>
              </div>

              {/* Revenue Sources */}
              {protocol.revenue_source && protocol.revenue_source.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {protocol.revenue_source.map((source, idx) => (
                    <Badge key={idx} variant="outline" className="text-xs">
                      {source.replace('_', ' ')}
                    </Badge>
                  ))}
                </div>
              )}

              {/* Affiliate Buy Buttons */}
              <div className="pt-2 border-t space-y-2">
                <p className="text-xs text-muted-foreground">Buy on:</p>
                <div className="flex flex-wrap gap-2">
                  {protocol.token.available_on_swyftx && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-xs h-7"
                      onClick={() => window.open(AFFILIATE_LINKS.swyftx, '_blank')}
                    >
                      <ExternalLink className="h-3 w-3 mr-1" />
                      Swyftx
                    </Button>
                  )}
                  {protocol.token.available_on_coinspot && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-xs h-7"
                      onClick={() => window.open(AFFILIATE_LINKS.coinspot, '_blank')}
                    >
                      <ExternalLink className="h-3 w-3 mr-1" />
                      CoinSpot
                    </Button>
                  )}
                  {!protocol.token.available_on_swyftx && !protocol.token.available_on_coinspot && (
                    <span className="text-xs text-muted-foreground italic">
                      Not available on AU exchanges
                    </span>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
